import React, { useState, useEffect, useCallback, useRef } from "react";
import { CaptchaSection } from "../../common/components";
import { LOAN_TYPE_OPTIONS, AUTHORIZATION_TEXT } from "./constants/formOptions";
import {sendApplyOnlineOtp, verifyApplyOnlineOtp,submitApplyOnlineRequest,} from "./services/applyOnlineService";
import "../../common/styles/index.css";
import "./styles/apply-online-form.css";
import {getCleanErrorMessage} from "../../common/config";
import OtpModal from "../../common/components/OtpModal";
import { setCookie } from "../../common/services/formsCookie";

const LEAD_VISITOR_NAME_COOKIE_KEY = "leadVisitorName";
const LEAD_VISITOR_LOAN_TYPE_COOKIE_KEY = "leadVisitorLoanType";

function ApplyOnlineApp() {
  const errorRef = useRef(null);
  const [formData, setFormData] = useState({
    fullName: "",
    emailId: "",
    pincode: "",
    loanType: "",
    phoneNumber: "",
    otp: "",
    isAuthorized: false,
  });

  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaValue, setCaptchaValue] = useState("");
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);

  useEffect(() => {
    if ((submitSuccess || globalError) && errorRef.current) {
      errorRef.current.scrollIntoView({
           behavior: "smooth",
           block: "center",
         });
    }
  }, [submitSuccess, globalError]);

  useEffect(() => {
    document.body.style.overflow = showOtpModal ? "hidden" : "auto";
  }, [showOtpModal]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "phoneNumber") {
      const cleanValue = value.replaceAll(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, phoneNumber: cleanValue, otp: "" }));
      setIsOtpVerified(false);
      setGlobalError("");
    } else if (name === "pincode") {
      const cleanValue = value.replaceAll(/\D/g, "").slice(0, 6);
      setFormData((prev) => ({ ...prev, [name]: cleanValue }));
    } else if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (name === "fullName") {
      setCookie(LEAD_VISITOR_NAME_COOKIE_KEY, value.trim());
    } else if (name === "loanType") {
      setCookie(LEAD_VISITOR_LOAN_TYPE_COOKIE_KEY, value);
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCaptchaChange = (inputValue, generatedValue) => {
    setCaptchaInput(inputValue);
    setCaptchaValue(generatedValue);

    if (errors.captcha) {
      setErrors((prev) => ({ ...prev, captcha: "" }));
    }
  };

  // Send OTP handler
  const handleSendOtp = useCallback(async () => {
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (formData.phoneNumber.length !== 10) {
      newErrors.phoneNumber = "Phone number must be 10 digits";
    } else if (!/^[6-9]\d{9}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Please enter a valid mobile number";
    }
    // Validate captcha before sending OTP
    if (!captchaInput || captchaInput !== captchaValue) {
      setErrors((prev) => ({
        ...prev,
        captcha: "Please enter the correct captcha",
      }));
      return false;
    }

    try {
      await sendApplyOnlineOtp(formData.phoneNumber);
      setGlobalError("");
      return true;
    } catch (error) {
      setGlobalError(error.message);
      return false;
    }
  }, [formData.phoneNumber, captchaInput, captchaValue]);

  // Verify OTP handler
  const handleVerifyOtp = useCallback(
    async (otp) => {
      try {
        const res = await verifyApplyOnlineOtp(otp, formData.phoneNumber);
        if (res?.Error?.ErrorCode) {
          throw new Error(res.Error.ErrorMessage || "Incorrect OTP");
        }
        setIsOtpVerified(true);
        setGlobalError("");
        setErrors((prev) => ({ ...prev, otp: "" }));
        return true;
      } catch (error) {
        setIsOtpVerified(false);
        setGlobalError(`${error.message}_${Date.now()}`);
        return false;
      }
    },
    [formData.phoneNumber],
  );

  const emailRegex = new RegExp(import.meta.env.VITE_EMAIL_REGEX);
  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.emailId.trim()) {
      newErrors.emailId = "Email is required";
    } else if (!emailRegex.test(formData.emailId)) {
      newErrors.emailId = "Please enter a valid email address";
    }

    if (formData.pincode?.length !== 6) {
      newErrors.pincode = "Please enter a valid 6-digit pincode";
    }

    if (!formData.loanType) {
      newErrors.loanType = "Please select a loan type";
    }

    if (
      !captchaInput ||
      captchaInput.toLowerCase() !== captchaValue.toLowerCase()
    ) {
      newErrors.captcha = "Please enter the correct captcha";
    }
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (formData.phoneNumber.length !== 10) {
      newErrors.phoneNumber = "Phone number must be 10 digits";
    } else if (!/^[6-9]\d{9}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Please enter a valid mobile number";
    }

    if (!isOtpVerified) {
      newErrors.otp = "Please verify OTP";
    }

    if (!formData.isAuthorized) {
      newErrors.isAuthorized = "Please accept the authorization";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
       setTimeout(() => {
            const firstError = document.querySelector(
              ".scl-form-field-error"
            );

            firstError?.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }, 0);

          return;
        }
    setIsSubmitting(true);
    setGlobalError("");
    try {
      await submitApplyOnlineRequest(formData);
      setSubmitSuccess(true);
    } catch (error) {
      setGlobalError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      fullName: "",
      emailId: "",
      pincode: "",
      loanType: "",
      phoneNumber: "",
      otp: "",
      isAuthorized: false,
    });
    setCaptchaInput("");
    setIsOtpVerified(false);
    setErrors({});
    setGlobalError("");
    setSubmitSuccess(false);
     window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
  };

  // Success view
  if (submitSuccess) {
    return (
      <div className="scl-apply-online-form scl-apply-online-form--success">
        <div className="scl-apply-online-form__success-icon">✓</div>
        <h2>Thank You!</h2>
        <p>
          Your request has been submitted successfully. Our team will contact
          you shortly.
        </p>
        <button
          type="button"
          className="scl-form-btn scl-form-btn--primary"
          onClick={resetForm}
        >
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <div className="scl-apply-online-form">
      {globalError && (
        <div ref={errorRef} className="scl-form-error-message">{getCleanErrorMessage(globalError)}</div>
      )}
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-content">
              {/* Full Name */}
          <div className="scl-form-group scl-grievance-form__question">
            <label className="scl-grievance-form__question-label small-body neutral1000">
              Full Name
            </label>

            <div className="input-box">
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Full Name"
                className={`scl-form-input ${errors.fullName ? "scl-form-input--error" : ""}`}
              />
            </div>

            {errors.fullName && (
              <span className="scl-form-field-error">{errors.fullName}</span>
            )}
          </div>

          <div className="scl-form-group scl-grievance-form__question">
            <label
              htmlFor="emailId"
              className="scl-grievance-form__question-label small-body neutral1000"
            >
              Email
            </label>

            <div className="input-box">
              <input
                type="email"
                name="emailId"
                value={formData.emailId}
                onChange={handleInputChange}
                placeholder="Email ID"
                className={`scl-form-input ${errors.emailId ? "scl-form-input--error" : ""}`}
              />
            </div>

            {errors.emailId && (
              <span className="scl-form-field-error">{errors.emailId}</span>
            )}

            <div className="scl-form-field-info">
              <img
                src="/documents/d/global/form-info"
                width={16}
                height={16}
                alt="info"
              />
              <span>We’ll use your email to respond to your query.</span>
            </div>
          </div>

          <div className="scl-form-group scl-grievance-form__question">
            <label className="scl-grievance-form__question-label small-body neutral1000">
              Pin code
            </label>

            <div className="input-box">
              <input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={handleInputChange}
                placeholder="Pincode"
                className={`scl-form-input ${errors.pincode ? "scl-form-input--error" : ""}`}
                maxLength={6}
              />
            </div>

            {errors.pincode && (
              <span className="scl-form-field-error">{errors.pincode}</span>
            )}
          </div>

          <div className="scl-form-group scl-grievance-form__question">
            <label className="scl-grievance-form__question-label small-body neutral1000">
              Loan Type
            </label>

            <select
              name="loanType"
              value={formData.loanType}
              onChange={handleInputChange}
              className={`scl-form-select ${errors.loanType ? "scl-form-select--error" : ""}`}
            >
              <option value="">Loan Types</option>
              {LOAN_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {errors.loanType && (
              <span className="scl-form-field-error">{errors.loanType}</span>
            )}
          </div>

          <div className="scl-grievance-form__question">
            <label
              htmlFor="captcha"
              className="scl-grievance-form__question-label"
            >
              Captcha
            </label>

            <CaptchaSection
              value={captchaInput}
              onChange={handleCaptchaChange}
              error={errors.captcha}
            />
          </div>

          {/* Phone Number */}
          <div className="scl-form-group scl-grievance-form__question">
            <label className="scl-grievance-form__question-label small-body neutral1000">
              Phone Number
            </label>

            <div className="scl-split-input">
              <span className="scl-split-input__action scl-split-input__prefix">
                <img
                  src="/documents/d/global/flag-of-india"
                  alt="India"
                  width={24}
                  height={16}
                />
                +91
              </span>

              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                placeholder="Phone Number"
                inputMode="numeric"
                pattern="[0-9]*"
                onChange={handleInputChange}
                className={`scl-split-input__field  ${errors.phoneNumber ? "scl-form-input--error" : ""}`}
                maxLength={10}
              />

              <button
                type="button"
                className={`scl-otp-btn scl-form-inline-btn ${
                  isOtpVerified ? "verified" : ""
                }`}
                disabled={
                  !/^[6-9]\d{9}$/.test(formData.phoneNumber) || isOtpVerified
                }
                onClick={async () => {
                  const success = await handleSendOtp();
                  if (success) {
                    setGlobalError("");
                    setShowOtpModal(true);
                  }
                }}
              >

                {isOtpVerified ? (
                  <>
                    <img
                      src="/documents/d/global/check-circle-green-tick"
                      width="16"
                      height="16"
                      alt="verified"
                    />
                    Verified
                  </>
                ) : (
                  "Send OTP"
                )}
              </button>
            </div>
            {errors.phoneNumber && (
                            <span className="scl-form-field-error">
                              {errors.phoneNumber}
                            </span>
                      )}
          </div>

          <div className="scl-form-group">
            <label
              className={`scl-form-checkbox ${errors.isAuthorized ? "scl-form-checkbox--error" : ""}`}
            >
              <input
                type="checkbox"
                name="isAuthorized"
                checked={formData.isAuthorized}
                onChange={handleInputChange}
              />
              <span className="scl-form-checkbox__box"></span>
              <span className="scl-form-checkbox__label regular-body regular">
                {AUTHORIZATION_TEXT}
              </span>
            </label>
            {errors.isAuthorized && (
              <span className="scl-form-field-error">{errors.isAuthorized}</span>
            )}
          </div>
        </div>

        <div className="scl-form-actions apply-online-button-container">
          <button
            type="submit"
            className="btn primary-button scl-form-btn--primary m-auto apply-online-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>
      {showOtpModal && (
        <OtpModal
          phoneNumber={formData.phoneNumber}
          onSendOtp={handleSendOtp}
          onVerifyOtp={handleVerifyOtp}
          otpError={globalError}
          onClose={() => {
            setShowOtpModal(false);
            setFormData((prev) => ({ ...prev, otp: "" }));
          }}
        />
      )}
    </div>
  );
}

export default ApplyOnlineApp;
